import type { TSchema } from '@sinclair/typebox';
import type { CustomComponent } from './createComponent';
import type { ComponentDefinition, ReportComponentDefinition } from '../types';
import type { DocxRendererId } from '../renderers/types';
import { type ThemeConfig } from '../styles';
import { resolveBuiltInTheme } from '../styles/theme-resolver';
import { hasTheme } from '../templates/themes';
import { resolveThemeContext } from '../core/generationContext';
import type { GenerationWarning } from '@json-to-office/shared-docx';
import type { ServicesConfig, FontRuntimeOpts } from '@json-to-office/shared';
import type {
  ExtendedReportComponent,
  DocumentGeneratorBuilder,
  GenerateOptions,
  GenerateFileOptions,
  BufferGenerationResult,
  FileGenerationResult,
  StandardDefinitionResult,
  QualityPreparationResult,
  ValidationResult,
  GenerationValidationOptions,
} from './types';
import {
  validateDocument,
  cleanComponentProps,
  ComponentValidationError,
  DuplicateComponentError,
} from './validation';
import { UnknownPreservedComponentError } from '@json-to-office/shared/plugin';
import { resolveComponentVersion } from './version-resolver';
import { generatePluginDocumentSchema, exportPluginSchema } from './schema';
import { generateBufferViaIr } from '../core/generateFromIr';
import { normalizeDocument } from '../json/normalizer';
import { expandBlocksWithPlugins } from '../blocks/document';
import { prepareDocxQualityDocument } from '../quality/facts';
import { validateComponentProps } from './validation';

/**
 * Options for creating a document generator
 */
export interface DocumentGeneratorOptions {
  /** Default theme used when no custom or built-in theme matches */
  theme?: ThemeConfig;
  /** Custom themes keyed by name, resolved per-document via document.props.theme */
  customThemes?: Record<string, ThemeConfig>;
  /** Enable debug logging */
  debug?: boolean;
  /** External service configuration (e.g. Highcharts export server) */
  services?: ServicesConfig;
  /** Font resolution options — extraEntries, Google Fonts config, onResolved hook. */
  fonts?: FontRuntimeOpts;
  /**
   * Default validation behavior for every generateBuffer/generateFile
   * call. Per-call `options.validation` overrides these. Validation is on by
   * default; pass `{ enabled: false }` to opt out.
   */
  validation?: GenerationValidationOptions;
  /** Normalize volatile OOXML values for byte-identical output. Defaults true. */
  deterministic?: boolean;
  /** Default build timestamp for generated metadata. */
  generatedAt?: string | Date;
  /**
   * Directory that relative asset paths (image `path` props) resolve against.
   * Per-call `options.baseDir` overrides it; defaults to `process.cwd()`
   * when neither is set (#142).
   */
  baseDir?: string;
  /**
   * Backend that turns the compiled document into bytes.
   *
   * Defaults to `docxjs`, which is what every existing caller gets.
   * `office-open` is experimental and opt-in: it declares a subset of features
   * and fails before rendering on anything outside it.
   */
  renderer?: DocxRendererId;
  /**
   * Rasterize a PNG fallback for each inline SVG. Defaults to true.
   *
   * Only readers older than Word 2016 draw that raster; everything current
   * draws the vector. Producing it dominates the render of a document whose
   * artwork is many small SVGs, so it can be turned off.
   */
  svgRasterFallback?: boolean;
}

/**
 * Internal state held by each builder instance
 */
interface BuilderState {
  components: readonly CustomComponent<any, any, any>[];
  componentNames: Set<string>;
  theme?: ThemeConfig;
  customThemes?: Record<string, ThemeConfig>;
  debug: boolean;
  services?: ServicesConfig;
  fonts?: FontRuntimeOpts;
  validation?: GenerationValidationOptions;
  deterministic: boolean;
  generatedAt?: string | Date;
  baseDir?: string;
  renderer?: DocxRendererId;
  /**
   * Rasterize a PNG fallback for each inline SVG. Defaults to true.
   *
   * Only readers older than Word 2016 draw that raster; everything current
   * draws the vector. Producing it dominates the render of a document whose
   * artwork is many small SVGs, so it can be turned off.
   */
  svgRasterFallback?: boolean;
}

/**
 * Create the builder implementation with the given state
 */
function createBuilderImpl<
  TComponents extends readonly CustomComponent<any, any, any>[],
>(state: BuilderState): DocumentGeneratorBuilder<TComponents> {
  // Create component map for quick lookup
  const componentMap = new Map(state.components.map((c) => [c.name, c]));

  /**
   * Resolve theme for a document: customThemes → doc-named built-in →
   * constructor theme → built-in resolver fallback.
   *
   * A document explicitly naming a known built-in gets it; the constructor
   * `theme` object fills in when the document names nothing or names
   * something nothing recognizes (#141). The `authored` guard on the
   * built-in step matters twice over: the unauthored 'consulting' default
   * must not shadow the constructor theme, and an authored UNKNOWN name
   * must still reach the constructor theme rather than silently rendering
   * the resolver's consulting fallback.
   */
  function resolveDocumentTheme(
    themeName: string,
    warnings: GenerationWarning[] | undefined,
    authored: boolean
  ): ThemeConfig {
    if (state.customThemes) {
      if (state.customThemes[themeName]) {
        return state.customThemes[themeName];
      }
      const key = Object.keys(state.customThemes).find(
        (k) => k.toLowerCase() === themeName.toLowerCase()
      );
      if (key) {
        return state.customThemes[key];
      }
    }
    if (authored && hasTheme(themeName)) {
      return resolveBuiltInTheme(themeName, {
        customThemes: state.customThemes,
        warnings,
      });
    }
    if (state.theme) {
      return state.theme;
    }
    return resolveBuiltInTheme(themeName, {
      customThemes: state.customThemes,
      warnings,
    });
  }

  /**
   * Process custom components in the document.
   *
   * Returns two parallel trees built in a single pass:
   * - `standard`: fully expanded — every custom component resolved to standard primitives.
   * - `preserved`: partially expanded — nodes whose name is in `preserveSet` are kept
   *   verbatim (children NOT recursed); everything else is expanded the same as `standard`.
   *
   * `render()` always runs, even for preserved components, because the docx pipeline
   * builds from `standard` and we still need their warnings/font registrations.
   */
  async function processDocumentComponents(
    components: ComponentDefinition[],
    preserveSet: ReadonlySet<string> | undefined,
    warningsCollector: GenerationWarning[],
    resolvedTheme: ThemeConfig,
    validateEmitted:
      | ((emitted: ComponentDefinition[], componentLabel: string) => void)
      | undefined,
    depth = 0
  ): Promise<{
    standard: ComponentDefinition[];
    preserved: ComponentDefinition[];
  }> {
    if (depth > 20) {
      throw new Error(
        'Maximum component nesting depth exceeded (20). Check for circular component references.'
      );
    }
    const standardOut: ComponentDefinition[] = [];
    const preservedOut: ComponentDefinition[] = [];

    for (const componentData of components) {
      // Safe type narrowing for custom component detection
      const componentName = (componentData as { name?: string })?.name;

      if (!componentName) {
        standardOut.push(componentData);
        preservedOut.push(componentData);
        continue;
      }

      const customComponent = componentMap.get(componentName);

      if (customComponent) {
        // This is a custom component - validate and process it
        try {
          const componentWithName = componentData as {
            name: string;
            version?: string;
            props?: unknown;
            children?: unknown;
          };

          if (!componentWithName.props) {
            throw new Error(
              `Custom component '${componentName}' must have a 'props' property. Use format: { name: '${componentName}', props: {...} }`
            );
          }

          // Resolve the correct version entry
          const versionEntry = resolveComponentVersion(
            customComponent.name,
            customComponent.versions,
            componentWithName.version
          );

          // Validate and clean the props against the resolved version's schema
          const cleanedProps = cleanComponentProps(
            versionEntry,
            componentWithName.props
          );

          // Process nested children if this is a container.
          // For the standard tree we need the fully-expanded children to feed render().
          let nestedChildren: unknown[] | undefined;
          if (
            componentWithName.children &&
            Array.isArray(componentWithName.children)
          ) {
            const nested = await processDocumentComponents(
              componentWithName.children as ComponentDefinition[],
              preserveSet,
              warningsCollector,
              resolvedTheme,
              validateEmitted,
              depth + 1
            );
            nestedChildren = nested.standard;
          }

          // Create addWarning callback for this component
          const versionLabel = componentWithName.version
            ? `${customComponent.name}@${componentWithName.version}`
            : customComponent.name;

          const addWarning = (
            message: string,
            context?: Record<string, unknown>
          ) => {
            warningsCollector.push({
              component: versionLabel,
              message,
              severity: 'warning',
              context,
            });
          };

          // Call the render function with context object
          const result = await versionEntry.render({
            props: cleanedProps,
            theme: resolvedTheme,
            addWarning,
            children: nestedChildren,
          });

          // Ensure result is an array
          const resultComponents = (
            Array.isArray(result) ? result : [result]
          ) as ComponentDefinition[];

          // Validate the standard tree this render() emitted, applying the same
          // schema gate authored standard components pass through up front. The
          // pre-expansion pass never saw these nodes, so without this an invalid
          // prop produced by a plugin would ride through into standardDefinition
          // and only surface when that output is validated separately.
          if (validateEmitted) {
            validateEmitted(resultComponents, versionLabel);
          }

          // Recursively process the result in case it contains more custom components
          const processedResult = await processDocumentComponents(
            resultComponents,
            preserveSet,
            warningsCollector,
            resolvedTheme,
            validateEmitted,
            depth + 1
          );
          standardOut.push(...processedResult.standard);

          if (preserveSet && preserveSet.has(componentName)) {
            // Keep this custom node verbatim in the preserved tree.
            // Per spec: do NOT recurse into authored children — leave the
            // entire subtree as the user wrote it.
            preservedOut.push(componentData);
          } else {
            // Non-preserved custom: emit the recursed expansion (which itself
            // honors preserveSet for any nested customs).
            preservedOut.push(...processedResult.preserved);
          }

          if (state.debug) {
            console.log(
              `Processed custom component '${versionLabel}':`,
              processedResult.standard
            );
          }
        } catch (error) {
          if (error instanceof ComponentValidationError) {
            throw error;
          }
          throw new Error(
            `Error processing custom component '${customComponent.name}': ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      } else {
        // This is a standard component or nested container - process recursively
        if (
          'children' in componentData &&
          Array.isArray(componentData.children)
        ) {
          const processedNested = await processDocumentComponents(
            componentData.children,
            preserveSet,
            warningsCollector,
            resolvedTheme,
            validateEmitted,
            depth + 1
          );
          standardOut.push({
            ...componentData,
            children: processedNested.standard,
          });
          preservedOut.push({
            ...componentData,
            children: processedNested.preserved,
          });
        } else {
          standardOut.push(componentData);
          preservedOut.push(componentData);
        }
      }
    }

    return { standard: standardOut, preserved: preservedOut };
  }

  /**
   * Add a custom component to the generator
   */
  function addComponent<TNewComponent extends CustomComponent<any, any, any>>(
    component: TNewComponent
  ): DocumentGeneratorBuilder<readonly [...TComponents, TNewComponent]> {
    if (!component.name) {
      throw new Error('Component name is required');
    }

    if (state.componentNames.has(component.name)) {
      throw new DuplicateComponentError(component.name);
    }

    // Create NEW immutable state
    const newComponentNames = new Set(state.componentNames);
    newComponentNames.add(component.name);

    const newState: BuilderState = {
      components: [...state.components, component],
      componentNames: newComponentNames,
      theme: state.theme,
      customThemes: state.customThemes,
      debug: state.debug,
      services: state.services,
      fonts: state.fonts,
      validation: state.validation,
      deterministic: state.deterministic,
      generatedAt: state.generatedAt,
      baseDir: state.baseDir,
      renderer: state.renderer,
      svgRasterFallback: state.svgRasterFallback,
    };

    // Return NEW builder with expanded type
    return createBuilderImpl<readonly [...TComponents, TNewComponent]>(
      newState
    );
  }

  /**
   * Resolve and validate the preserve set for a per-call options object.
   * Returns `undefined` when the caller did not opt in.
   * Throws `UnknownPreservedComponentError` when any listed name is not registered.
   */
  function resolvePreserveSet(
    options?: GenerateOptions
  ): ReadonlySet<string> | undefined {
    const list = options?.preserveCustomComponents;
    if (!list || list.length === 0) {
      return undefined;
    }
    const registered = new Set(state.componentNames);
    const unknown = list.filter((n) => !registered.has(n));
    if (unknown.length > 0) {
      throw new UnknownPreservedComponentError(
        unknown,
        Array.from(state.componentNames)
      );
    }
    return new Set(list);
  }

  /**
   * Shared front half of the pipeline: validate, resolve theme context,
   * expand custom components, normalize. Everything `standardDefinition`
   * needs and nothing more — fonts, structure, layout, rendering (and the
   * visual rasterization that rides on it) all happen after this point.
   */
  async function expandDocument(
    document: ExtendedReportComponent<TComponents>,
    options?: GenerateOptions,
    /**
     * Always take the combined block/plugin expansion, which keeps a source
     * map back to each invocation, even for a document that defines no
     * blocks. Quality preparation needs the map; rendering does not.
     */
    provenance = false
  ): Promise<{
    modedRoot: ReportComponentDefinition;
    modedTheme: ThemeConfig;
    themeName: string;
    processed: {
      standard: ComponentDefinition[];
      preserved: ComponentDefinition[];
    };
    modedDoc: ReportComponentDefinition;
    blockPaths?: readonly string[];
    sourceMap?: Readonly<Record<string, string>>;
    pluginOutputs?: readonly string[];
    warnings: GenerationWarning[];
    preserveSet: ReadonlySet<string> | undefined;
  }> {
    const preserveSet = resolvePreserveSet(options);

    // Cast to ReportComponentDefinition for internal processing. A root
    // written without a `props` key validates clean, so default it to `{}`
    // — otherwise every downstream `props.*` read throws. Only `undefined`
    // is defaulted, so a malformed `props: null` still reaches the
    // validator. Matches core/generator.ts.
    const rootIn = document as unknown as ReportComponentDefinition;
    const internalDocument: ReportComponentDefinition =
      rootIn.props === undefined ? { ...rootIn, props: {} } : rootIn;
    const renderer =
      options?.renderer ?? state.renderer ?? internalDocument.renderer;
    const validationDocument =
      renderer === undefined
        ? internalDocument
        : { ...internalDocument, renderer };

    // Validate the document first (plugin-aware), unless disabled. Throwing
    // here stops a malformed document from silently building into a corrupt
    // or incomplete DOCX.
    const vOpts: GenerationValidationOptions = {
      ...state.validation,
      ...options?.validation,
    };
    if (vOpts.enabled !== false) {
      const result = validateDocument(
        validationDocument,
        state.components as unknown as CustomComponent<TSchema>[],
        { allowUnknownFields: vOpts.allowUnknownFields }
      );
      if (!result.valid) {
        throw new ComponentValidationError(
          (result.errors ?? []).map((e) => ({
            path: e.path ?? '',
            message: e.message,
          })),
          internalDocument
        );
      }
    }

    // Re-apply the same gate to the tree each custom component's render()
    // emits. The pre-expansion pass above only saw authored nodes, so a
    // standard component produced by a plugin would otherwise reach
    // standardDefinition unchecked. We validate the emitted nodes in their
    // pre-normalization form — exactly how authored nodes are validated —
    // by reusing the already-validated document props as a wrapper, so the
    // only new errors come from the emitted children. Undefined when
    // validation is disabled, which short-circuits the boundary check.
    const validateEmitted =
      vOpts.enabled === false
        ? undefined
        : (
            emitted: ComponentDefinition[],
            componentLabel: string,
            sourcePath?: string
          ) => {
            const result = validateDocument(
              {
                ...internalDocument,
                ...(renderer !== undefined ? { renderer } : {}),
                children:
                  sourcePath === undefined
                    ? emitted
                    : [{ name: 'section', props: {}, children: emitted }],
              },
              state.components as unknown as CustomComponent<TSchema>[],
              { allowUnknownFields: vOpts.allowUnknownFields }
            );
            if (!result.valid) {
              throw new ComponentValidationError(
                (result.errors ?? []).map((e) => ({
                  path:
                    sourcePath === undefined
                      ? e.path ?? ''
                      : (e.path ?? '').replace(
                          /^\/children\/0\/children\/\d+/,
                          sourcePath
                        ),
                  message: `custom component '${componentLabel}' emitted invalid output — ${e.message}`,
                })),
                emitted
              );
            }
          };

    // Initialize warnings collector
    const warnings: GenerationWarning[] = [];

    // Props defaulting, theme resolution (customThemes → doc-named
    // built-in → constructor theme → built-in fallback, see
    // resolveDocumentTheme), in-document overrides and export-mode pre-pass —
    // shared with the core pipeline so the two cannot drift (see
    // core/generationContext.ts). The pre-pass runs BEFORE custom-component
    // expansion so components reading `theme.fonts.*` during render see the
    // substituted names, not the original non-safe ones.
    const {
      document: modedRoot,
      theme: modedTheme,
      themeName,
    } = resolveThemeContext(internalDocument, {
      customThemes: state.customThemes,
      fonts: state.fonts,
      warnings,
      resolveNamedTheme: resolveDocumentTheme,
    });

    // Document-local JSON and registered code share one bounded expansion.
    if (modedRoot.props.blocks !== undefined || provenance) {
      const expanded = await expandBlocksWithPlugins(
        modedRoot,
        modedTheme,
        new Set(componentMap.keys()),
        async (component, path) => {
          const custom = componentMap.get(String(component.name))!;
          const version = resolveComponentVersion(
            custom.name,
            custom.versions,
            component.version as string | undefined
          );
          const checked = validateComponentProps(
            version,
            component.props ?? {},
            custom.name,
            { clean: vOpts.allowUnknownFields === true }
          );
          if (!checked.valid)
            throw new ComponentValidationError(
              (checked.errors ?? []).map((e) => ({ path, message: e.message })),
              component
            );
          const output = await version.render({
            props: cleanComponentProps(version, component.props ?? {}),
            theme: modedTheme,
            children: component.children as unknown[] | undefined,
            addWarning: (message, context) =>
              warnings.push({
                component: custom.name,
                message,
                severity: 'warning',
                context,
              }),
          });
          const emitted = (
            Array.isArray(output) ? output : [output]
          ) as ComponentDefinition[];
          validateEmitted?.(emitted, custom.name, path);
          return emitted;
        },
        preserveSet
      );
      const [modedDoc] = normalizeDocument(expanded.document);
      return {
        modedRoot,
        modedTheme,
        themeName,
        modedDoc,
        sourceMap: expanded.sourceMap,
        blockPaths: expanded.blocks,
        pluginOutputs: expanded.pluginOutputs,
        warnings,
        preserveSet,
        processed: {
          standard: modedDoc.children ?? [],
          preserved: expanded.preserved.children ?? [],
        },
      };
    }

    // Process custom components to convert them to standard components.
    // Builds standard (fully expanded) and preserved (partial) trees in one pass.
    const processed = await processDocumentComponents(
      modedRoot.children || [],
      preserveSet,
      warnings,
      modedTheme,
      validateEmitted
    );

    // Create a new document definition with processed components
    const processedDocument: ReportComponentDefinition = {
      ...modedRoot,
      children: processed.standard,
    };

    // Normalize components (handle shorthand notations and nested structures)
    // We bypass JSON validation since we've already validated with custom schemas
    const [modedDoc] = normalizeDocument(processedDocument);

    return {
      modedRoot,
      modedTheme,
      themeName,
      processed,
      modedDoc,
      warnings,
      preserveSet,
    };
  }

  /**
   * Compute only the post-expansion standard definition. Runs validation,
   * theme resolution, custom-component expansion, and normalization — but
   * skips font resolution, layout, rendering, and packaging, so it never
   * touches external services (no visual rasterization, no chart export).
   * Orders of magnitude cheaper than `generateBuffer()` when you only need the
   * JSON tree.
   */
  async function expandStandardDefinition(
    document: ExtendedReportComponent<TComponents>,
    options?: GenerateOptions
  ): Promise<StandardDefinitionResult> {
    try {
      const { modedDoc, warnings, sourceMap } = await expandDocument(
        document,
        options
      );
      return {
        standardDefinition: modedDoc,
        ...(sourceMap && { sourceMap }),
        warnings: warnings.length > 0 ? warnings : null,
      };
    } catch (error) {
      if (state.debug) {
        console.error('Document expansion error:', error);
      }
      throw error;
    }
  }

  /**
   * The quality model of a document with its registered components expanded
   * the way generation expands them: the same validation, theme context and
   * bounded block and plugin expansion, so a fact about a plugin's output is
   * reported at the invocation that emitted it, and a fact inside a block at
   * the slot the author filled. Nothing renders.
   */
  async function prepareQuality(
    document: ExtendedReportComponent<TComponents>,
    options?: GenerateOptions
  ): Promise<QualityPreparationResult> {
    const {
      modedRoot,
      modedTheme,
      themeName,
      modedDoc,
      sourceMap,
      blockPaths,
      pluginOutputs,
      warnings,
    } = await expandDocument(document, options, true);
    const prepared = prepareDocxQualityDocument(
      document as unknown as ReportComponentDefinition,
      {
        context: { document: modedRoot, theme: modedTheme, themeName },
        expanded: {
          document: modedDoc,
          sourceMap: sourceMap ?? {},
          blocks: blockPaths ?? [],
          ...(pluginOutputs && { pluginOutputs }),
        },
        customThemes: state.customThemes,
        fonts: state.fonts,
        renderer: options?.renderer ?? state.renderer ?? modedRoot.renderer,
        warnings,
      }
    );
    return { prepared, warnings: warnings.length > 0 ? warnings : null };
  }

  /**
   * Render the tree a `prepareQuality` call already expanded. The prologue
   * and the plugins ran there: the prepared model carries the theme context
   * over the expanded document, so the IR pipeline skips both and the bytes
   * come from the tree the analysis judged.
   */
  async function generateBufferFromPrepared(
    prepared: NonNullable<GenerateOptions['prepared']>,
    options?: GenerateOptions
  ): Promise<BufferGenerationResult<TComponents>> {
    const { context } = prepared.model;
    const warnings: GenerationWarning[] = [];
    const baseDir = options?.baseDir ?? state.baseDir;
    const deterministic = options?.deterministic ?? state.deterministic;
    const generatedAt = options?.generatedAt ?? state.generatedAt;
    const renderer =
      options?.renderer ??
      state.renderer ??
      (prepared.renderer as DocxRendererId | undefined);
    const { buffer } = await generateBufferViaIr(context.document, {
      context,
      prepared,
      ...(state.services ? { services: state.services } : {}),
      ...(state.fonts ? { fonts: state.fonts } : {}),
      ...(baseDir !== undefined ? { baseDir } : {}),
      ...(deterministic !== undefined ? { deterministic } : {}),
      ...(generatedAt !== undefined ? { generatedAt } : {}),
      ...(renderer !== undefined ? { renderer } : {}),
      warnings,
    });
    return {
      buffer,
      warnings: warnings.length > 0 ? warnings : null,
      standardDefinition: context.document,
      preservedDefinition: undefined,
    };
  }

  /**
   * Generate a document as a `.docx` buffer.
   *
   * The custom components are expanded first, then the standard tree it
   * produced goes through the same DocxIR pipeline every other entry point
   * uses — a plugin document and a plain one are the same document by the time
   * anything renders.
   */
  async function generateBuffer(
    document: ExtendedReportComponent<TComponents>,
    options?: GenerateOptions
  ): Promise<BufferGenerationResult<TComponents>> {
    try {
      // A model prepared from this same document already carries the one
      // expansion the quality gate inspected — plugin output included — so
      // rendering reads it instead of running the plugins again. Preserved
      // components are the exception: they are the partially expanded tree,
      // which the prepared model does not keep.
      const reused =
        options?.prepared && resolvePreserveSet(options) === undefined
          ? options.prepared
          : undefined;
      if (reused) return await generateBufferFromPrepared(reused, options);
      const {
        modedRoot,
        modedTheme,
        themeName,
        processed,
        modedDoc,
        sourceMap,
        blockPaths,
        warnings,
        preserveSet,
      } = await expandDocument(document, options);

      const baseDir = options?.baseDir ?? state.baseDir;
      const deterministic = options?.deterministic ?? state.deterministic;
      const generatedAt = options?.generatedAt ?? state.generatedAt;
      const renderer =
        options?.renderer ?? state.renderer ?? modedRoot.renderer;

      const { buffer } = await generateBufferViaIr(modedDoc, {
        // The prologue already ran: the theme had to be resolved before custom
        // components expanded, because each one's `render` is handed it.
        context: {
          document: modedDoc,
          theme: modedTheme,
          themeName,
          ...(sourceMap !== undefined && { sourceMap }),
          ...(blockPaths !== undefined && { blockPaths }),
        },
        ...(state.services ? { services: state.services } : {}),
        ...(state.fonts ? { fonts: state.fonts } : {}),
        ...(baseDir !== undefined ? { baseDir } : {}),
        ...(deterministic !== undefined ? { deterministic } : {}),
        ...(generatedAt !== undefined ? { generatedAt } : {}),
        ...(renderer !== undefined ? { renderer } : {}),
        warnings,
      });

      // Build preservedDefinition iff the caller opted in. Reuses the same
      // doc shell as standardDefinition but with partially-expanded children.
      // Not normalized — preserved subtrees are meant to be "as authored".
      const preservedDefinition = preserveSet
        ? ({
            ...modedRoot,
            children: processed.preserved,
          } as unknown as ExtendedReportComponent<TComponents>)
        : undefined;

      return {
        buffer,
        warnings: warnings.length > 0 ? warnings : null,
        standardDefinition: modedDoc,
        preservedDefinition,
      };
    } catch (error) {
      if (state.debug) {
        console.error('Document generation error:', error);
      }
      throw error;
    }
  }

  /**
   * Generate a document and save to file
   */
  async function generateFile(
    document: ExtendedReportComponent<TComponents>,
    outputPath: string,
    options?: GenerateFileOptions
  ): Promise<FileGenerationResult<TComponents>> {
    const { buffer, warnings, standardDefinition, preservedDefinition } =
      await generateBuffer(document, options);
    const fs = await import('fs/promises');
    await fs.writeFile(outputPath, new Uint8Array(buffer));

    let preservedOutputPath: string | undefined;
    if (preservedDefinition !== undefined) {
      const path = await import('path');
      preservedOutputPath =
        options?.preservedOutputPath ??
        (() => {
          const ext = path.extname(outputPath);
          const base = ext ? outputPath.slice(0, -ext.length) : outputPath;
          return `${base}-preserved.json`;
        })();
      await fs.writeFile(
        preservedOutputPath,
        JSON.stringify(preservedDefinition, null, 2),
        'utf8'
      );
    }

    return {
      warnings,
      standardDefinition,
      preservedDefinition,
      preservedOutputPath,
    };
  }

  /**
   * Get registered component names
   */
  function getComponentNames(): string[] {
    return Array.from(state.componentNames);
  }

  /**
   * Validate a document without generating it
   */
  function validate(
    document: ExtendedReportComponent<TComponents>
  ): ValidationResult {
    try {
      // Cast to ReportComponentDefinition for internal validation
      const internalDocument = document as unknown as ReportComponentDefinition;
      const result = validateDocument(
        internalDocument,
        state.components as unknown as CustomComponent<TSchema>[]
      );
      if (result.valid) {
        return { valid: true };
      }
      return {
        valid: false,
        errors: (result.errors ?? []).map((e) => ({
          path: e.path ?? '',
          message: e.message,
        })),
      };
    } catch (error) {
      return {
        valid: false,
        errors: [
          {
            path: 'document',
            message: error instanceof Error ? error.message : String(error),
          },
        ],
      };
    }
  }

  /**
   * @deprecated Read `standardDefinition` off `generateBuffer(...)` instead.
   * This wrapper expands every custom component just to surface the JSON tree,
   * so calling it alongside a generate doubles that work. Kept for backwards
   * compatibility; will be removed in a future major.
   */
  async function getStandardComponentsDefinition(
    document: ExtendedReportComponent<TComponents>
  ): Promise<ReportComponentDefinition> {
    const { modedDoc } = await expandDocument(document);
    return modedDoc;
  }

  /**
   * Generate the extended JSON schema for document validation
   */
  function generateSchema(includeStandardComponents = true): TSchema {
    return generatePluginDocumentSchema(
      state.components as unknown as CustomComponent<TSchema>[],
      includeStandardComponents
    );
  }

  /**
   * Export the extended JSON schema to a file
   */
  async function exportSchemaToFile(
    outputPath: string,
    options?: {
      includeStandardComponents?: boolean;
      prettyPrint?: boolean;
    }
  ): Promise<void> {
    await exportPluginSchema(
      state.components as unknown as CustomComponent<TSchema>[],
      outputPath,
      options
    );
  }

  // Return frozen builder object
  return Object.freeze({
    addComponent,
    generateBuffer,
    generateFile,
    expandStandardDefinition,
    prepareQuality,
    getComponentNames,
    validate,
    generateSchema,
    exportSchema: exportSchemaToFile,
    getStandardComponentsDefinition,
  });
}

/**
 * Create a document generator with chainable component registration.
 */
export function createDocumentGenerator(
  options: DocumentGeneratorOptions
): DocumentGeneratorBuilder<readonly []> {
  const initialState: BuilderState = {
    components: [],
    componentNames: new Set(),
    theme: options.theme,
    customThemes: options.customThemes,
    debug: options.debug ?? false,
    services: options.services,
    fonts: options.fonts,
    validation: options.validation,
    deterministic: options.deterministic ?? true,
    generatedAt: options.generatedAt,
    baseDir: options.baseDir,
    renderer: options.renderer,
    svgRasterFallback: options.svgRasterFallback,
  };

  return createBuilderImpl<readonly []>(initialState);
}
